/* La caja que comparten Input, Select y Textarea: misma altura que Button
   (`--h-control-md`), píldora, borde gray-300. El anillo de foco lo dibuja
   la caja con `:focus-within` (borde accent + 3px de accent-100), así que el
   control interno lleva `outline: none; box-shadow: none` para no duplicar el
   global de base.css. */
.caja {
  position: relative;
  display: flex;
  align-items: center;
  gap: var(--s-4);
  min-height: var(--h-control-md);
  padding: 0 var(--s-6);
  background: var(--surface-card);
  border: var(--bw-hair) solid var(--border-strong);
  border-radius: var(--r-control);
  transition:
    border-color var(--dur-fast) var(--ease-standard),
    box-shadow var(--dur-fast) var(--ease-standard),
    background var(--dur-fast) var(--ease-standard);
}
/* lg: la altura de un Button lg y el cuerpo en 14; el campo único de un formulario público (el login). */
.caja[data-size="lg"] {
  min-height: var(--h-control-lg);
  padding: 0 var(--s-7);
}
.caja[data-size="lg"] .control {
  font-size: var(--fs-sm);
}
@media (hover: hover) {
  .caja:not(:focus-within):not(:has(:disabled)):hover {
    border-color: var(--border-hover);
  }
}
.caja:focus-within {
  border-color: var(--border-brand);
  box-shadow: var(--ring-focus);
}
.caja[data-error] {
  border-color: var(--status-danger);
}
.caja[data-error]:focus-within {
  box-shadow: var(--ring-focus-danger);
}
.caja:has(:disabled) {
  background: var(--control-disabled-bg);
  border-color: var(--control-disabled-border);
  color: var(--control-disabled-text);
}

.control {
  flex: 1;
  /* Ocupa todo el alto de la caja (un clic en cualquier punto enfoca) sin poder
     medir más que ella: `stretch` y no `min-height: inherit`, que sumaba el
     borde y dejaba al input asomando por arriba y por abajo. */
  align-self: stretch;
  min-width: 0;
  width: 100%;
  height: auto;
  min-height: 0;
  border: none;
  background: transparent;
  outline: none;
  box-shadow: none;
  font-family: var(--font-body);
  font-size: var(--fs-xs);
  color: var(--text-strong);
  padding: 0;
}
/* El foco lo dibuja la caja. El control nunca pinta el suyo, gane quien gane el
   orden entre esta hoja y el `:focus-visible` global de base.css. */
.caja .control:focus,
.caja .control:focus-visible {
  outline: none;
  box-shadow: none;
}
.control::placeholder {
  color: var(--text-subtle);
}
.control:disabled {
  color: var(--control-disabled-text);
  cursor: not-allowed;
}
.control:disabled::placeholder {
  color: var(--control-disabled-text);
}
/* Sin las flechitas del sistema en los numéricos: el teclado numérico del
   celular sale de `inputMode`, que Input pone solo. */
.control[type="number"] {
  appearance: textfield;
  -moz-appearance: textfield;
}
.control[type="number"]::-webkit-outer-spin-button,
.control[type="number"]::-webkit-inner-spin-button {
  appearance: none;
  margin: 0;
}

.icono {
  display: inline-flex;
  color: var(--text-subtle);
}
.sufijo {
  font-family: var(--font-mono);
  font-variant-numeric: tabular-nums;
  font-size: var(--fs-2xs);
  color: var(--text-muted);
  white-space: nowrap;
}

/* Textarea: varias líneas no van en píldora; radio de panel y padding vertical. */
.caja[data-multilinea] {
  align-items: stretch;
  padding: var(--s-5) var(--s-6);
  border-radius: var(--r-panel);
}
.caja[data-multilinea] .control {
  min-height: calc(var(--h-control-md) * 2.8);
  line-height: var(--lh-relaxed);
  resize: vertical;
}

/* Select: la flecha es decorativa y va encima del padding derecho. */
.caja[data-select] {
  padding-right: 0;
}
.caja[data-select] .control {
  appearance: none;
  padding-right: var(--s-10);
  cursor: pointer;
}
.caja[data-select] .control:disabled {
  cursor: not-allowed;
}
/* Filtro (`Select variant="filter"`): una píldora que mide lo que su texto, en semibold tinta, con la
   flecha en línea; con algo elegido o abierta, el borde pasa a accent. */
.caja[data-filtro] {
  padding-right: var(--s-6);
}
.caja[data-filtro] .control {
  width: auto;
  padding-right: 0;
  font-weight: var(--fw-semibold);
  color: var(--text-strong);
}
.caja[data-filtro][data-activo],
.caja[data-filtro][data-abierto] {
  border-color: var(--border-brand);
}
.flecha {
  position: absolute;
  right: var(--s-6);
  top: 50%;
  transform: translateY(-50%);
  pointer-events: none;
  color: var(--text-muted);
  transition: transform var(--dur-fast) var(--ease-standard);
}
.caja:has(:disabled) .flecha {
  color: var(--control-disabled-border);
}
.caja[data-abierto] .flecha {
  transform: translateY(-50%) rotate(180deg);
}
.caja[data-filtro] .flecha {
  position: static;
  transform: none;
}
.caja[data-filtro][data-abierto] .flecha {
  transform: rotate(180deg);
}

@media (prefers-reduced-motion: reduce) {
  .caja,
  .flecha {
    transition: none;
  }
}

/* El contador de un Textarea: mono chiquito y gris; ámbar cuando queda poco lugar. */
.contador {
  font-family: var(--font-mono);
  font-variant-numeric: tabular-nums;
  font-size: var(--fs-3xs);
  color: var(--text-subtle);
}
.contador[data-warn] {
  color: var(--text-warning);
}
